import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay, format } from 'date-fns';

import { FlexBox } from '@sternum/styled-system';
import { ChevronUpIcon, Menu, Tag, Text12 } from '@sternum/ui';
import { namespaces } from '@sternum/web/services/i18n';
import { formattedDatesDifference } from '@sternum/web/utils/formattedDatesDifference';

import { DateRange, DateRangePicker } from '../DateRangePicker';
import { Icon } from '../Icon';

import { TimeRange } from './TimeRange.types';
import { TimeRangeUtils } from './TimeRange.utils';

import { DropdownStyled } from './TimeRangeDropdown.style';

export type TimeRangeDropdownOption = { value: TimeRange };

export interface TimeRangeDropdownProps {
  value: TimeRange;
  options: TimeRangeDropdownOption[];
  onChange: (value: TimeRange) => unknown;
  disabled?: boolean;
  rangeMinDate?: Date;
  rangeMaxDate?: Date;
  showRangeSelector?: boolean;
}

export const TimeRangeDropdown = forwardRef<HTMLDivElement, TimeRangeDropdownProps>(function TimeRangeDropdown(
  { value, options, onChange, disabled = false, rangeMinDate, rangeMaxDate, showRangeSelector = true },
  ref,
) {
  const { t } = useTranslation(namespaces.common);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isDateRangeVisible, setIsDateRangeVisible] = useState(false);
  const stringValue = TimeRangeUtils.serialize(value);

  const dateFrom = new Date(value.from || new Date());
  const dateTo = new Date(value.to || new Date());
  const datesDifference = formattedDatesDifference(dateFrom, dateTo);
  const datesDifferenceLength = datesDifference.length;
  const dropdownWidth = datesDifferenceLength > 2 ? 300 : 280;

  const handleChange = (newValue?: string) => {
    if (!newValue) return;

    const newTimeRange = TimeRangeUtils.deserialize(newValue);

    setIsDropdownOpen(false);
    setIsDateRangeVisible(false);
    onChange(newTimeRange);
  };

  const handleDropdownToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    const isInsideMenu = menuRef.current?.contains(e.target as Node);

    if (isDropdownOpen && isInsideMenu) return;

    setIsDropdownOpen((isOpen) => !isOpen);
  };

  const handleDateRangeChange = (range: DateRange) => {
    const roundEndDateToEndOfDay = endOfDay(range.endDate);

    onChange({
      from: range.startDate.getTime(),
      to: roundEndDateToEndOfDay.getTime(),
    });
    setIsDateRangeVisible(false);
    setIsDropdownOpen(false);
  };

  const renderOption = (range: TimeRange) => {
    return (
      <FlexBox gap={8} alignItems="center">
        <Tag variant="basic-filled" size="small" style={{ width: 38 }}>
          {t('timeRangeChip', { unit: range.unit, offset: range.offset })}
        </Tag>

        <Text12>{t('timeRangeLabel', { unit: range.unit, offset: range.offset })}</Text12>
      </FlexBox>
    );
  };

  const renderMenuItemForAllTime = (stringValue: string) => {
    return (
      <Menu.Item role="listitem" aria-label="time range value - {}" key={stringValue} value={stringValue}>
        <FlexBox gap={8} alignItems="center">
          <Tag variant="basic-filled" size="small" style={{ width: 38 }}>
            <Icon type="ClockIcon" />
          </Tag>

          <Text12>{t('allTimeRange')}</Text12>
        </FlexBox>
      </Menu.Item>
    );
  };

  const renderSelectedValue = (value: string) => {
    const range = TimeRangeUtils.deserialize(value);
    const timeFormat = 'MMM dd, hh:mm aaa';

    const renderContent = () => {
      if (value === '{}') {
        return (
          <>
            <Tag variant="basic-filled" size="small">
              <Icon type="ClockIcon" />
            </Tag>

            <Text12>{t('allTimeRange')}</Text12>
          </>
        );
      }

      if (range.unit) {
        return (
          <>
            <Tag variant="basic-filled" size="small">
              {t('timeRangeChip', { unit: range.unit, offset: range.offset })}
            </Tag>

            <Text12>{t('timeRangeLabel', { unit: range.unit, offset: range.offset })}</Text12>
          </>
        );
      }

      return (
        <>
          <Tag variant="basic-filled" size="small">
            {datesDifference}
          </Tag>
          <Text12>
            {format(dateFrom, timeFormat)} - {format(dateTo, timeFormat)}
          </Text12>
        </>
      );
    };

    return (
      <FlexBox role="presentation" aria-label={`selected time range - ${value}`} gap={8} alignItems="center">
        {renderContent()}
      </FlexBox>
    );
  };

  return (
    <DropdownStyled
      ref={ref}
      role="listbox"
      aria-label="time range select"
      size="small"
      value={stringValue}
      printValue={renderSelectedValue}
      onChange={handleChange}
      onClick={!disabled ? handleDropdownToggle : undefined}
      disabled={disabled}
      isOpen={isDropdownOpen || isDateRangeVisible}
      maxHeight={350}
      style={{ width: dropdownWidth }}
      localTheme={{
        customArrowOpenedIcon: () => <ChevronUpIcon />,
        customArrowClosedIcon: () => <ChevronUpIcon />,
      }}
      popupProps={{
        style: { width: dropdownWidth },
        offsetY: 4,
        onClickAway: () => {
          setIsDateRangeVisible(false);
          setIsDropdownOpen(false);
        },
      }}
    >
      <Menu ref={menuRef} role="listbox" aria-label="time range dropdown values">
        {isDateRangeVisible ? (
          <DateRangePicker
            initialValue={{
              startDate: dateFrom,
              endDate: dateTo,
            }}
            onChange={handleDateRangeChange}
            minDate={rangeMinDate}
            maxDate={rangeMaxDate}
          />
        ) : (
          <>
            {options.map(({ value: range }) => {
              const stringValue = TimeRangeUtils.serialize(range);

              if (stringValue === '{}') {
                return renderMenuItemForAllTime(stringValue);
              }

              return (
                <Menu.Item
                  role="listitem"
                  aria-label={`time range value - ${stringValue}`}
                  key={stringValue}
                  value={stringValue}
                >
                  {renderOption(range)}
                </Menu.Item>
              );
            })}

            {showRangeSelector && (
              <Menu.Item
                role="listitem"
                aria-label={`time range custom`}
                onClick={() => {
                  setIsDateRangeVisible(true);
                }}
              >
                <FlexBox gap={8} alignItems="center">
                  <Tag variant="basic-filled" size="small" style={{ width: 38 }}>
                    <Icon type="Activities2Icon" />
                  </Tag>

                  <Text12>{t('customRange')}</Text12>
                </FlexBox>
              </Menu.Item>
            )}
          </>
        )}
      </Menu>
    </DropdownStyled>
  );
});
